import { UserIcon } from '@heroicons/react/16/solid';
import { LinkIcon } from '@heroicons/react/20/solid';
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';

import { extractMarkdownImageUrls } from '@cavela/ui';
import { format } from 'date-fns';
import { useAuthStore } from 'src/entities/user/store/useAuthStore';
import { UserAvatar } from 'src/shared/components';
import { FileAttachment } from 'src/shared/components';
import { ROUTE_PATHS } from 'src/shared/config/routes';
import { Update } from 'src/shared/types/update';

import ImageGallery from 'src/shared/components/ImageGallery';
import { addToast, Spinner } from 'src/shared/components/ui';
import { ButtonV2, Tooltip, Typography } from 'src/shared/components/ui';

import useRouteProductId from '../../hooks/useRouteProductId';
import { useUpdateProductUpdate } from '../../hooks/useUpdateProductUpdate';
import getUpdateStatus from '../../utils/getUpdateStatus';
import isFileImage from '../../utils/isFileImage';
import { updateSupplierCloakLabel } from '../../utils/updateSupplierCloaks';
import ClarificationContent from './clarification-content/ClarificationContent';
import {
  getUpdateCreatorAvatar,
  getUpdateCreatorName
} from './getUpdateCreator';
import MarkdownContent from './markdown-content/MarkdownContent';
import ProductUpdatesInput from './ProductUpdatesInput';
import QuestionContent from './question-content/QuestionContent';
import UpdateThread from './UpdateThread';

interface ProductUpdateModalContentProps {
  update: Update;
  onScrollToBottom?: () => void;
}

const ProductUpdateModalContent: React.FC<ProductUpdateModalContentProps> = ({
  update,
  onScrollToBottom
}) => {
  const user = useAuthStore((state) => state.user);
  const selectedOrganization = useAuthStore(
    (state) => state.selectedOrganization
  );

  const isAnswered = Boolean(update?.question?.answer);
  const isQuestion = Boolean(update?.question);
  const isComplete = update.update_type === 'complete';

  const linkedBulkOrders = update?.entities?.bulk_orderings ?? [];
  const linkedSamples = update?.entities?.samplings ?? [];
  const supplierCloaks = updateSupplierCloakLabel(update);

  const updateMutation = useUpdateProductUpdate();

  const renderQuestion = () => {
    const question = update?.question;

    if (question) {
      return <QuestionContent update={update} />;
    }

    return null;
  };

  const renderClarification = () => {
    const clarification = update?.clarification;

    if (clarification) {
      return <ClarificationContent update={update} />;
    }

    return null;
  };

  const routeProductId = useRouteProductId();
  const onResolveUpdate = () => {
    updateMutation.mutate(
      {
        updateId: update.id,
        body: { relevant: false },
        productId: routeProductId as string,
        userId: user?.id as number
      },
      {
        onSuccess: () => {
          addToast({
            color: 'success',
            title: 'Update has been resolved.'
          });
        }
      }
    );
  };

  const withLine = useMemo(() => {
    return update?.clarification?.replies.length > 0 || isAnswered;
  }, [update?.clarification, isAnswered]);

  const getCreatorAvatar = () =>
    getUpdateCreatorAvatar(update, selectedOrganization);
  const getCreatorName = () => getUpdateCreatorName(update);

  /**
   * Collect images from all sources (message markdown, clarification files,
   * question embedded files, update embedded files) and deduplicate by URL so
   * that exactly one ImageGallery is rendered regardless of how many sources
   * reference the same image.
   */
  const allImages = useMemo(() => {
    const seen = new Set<string>();
    const images: {
      url: string;
      description?: string | null;
      name?: string | null;
    }[] = [];

    const add = (
      url: string,
      meta?: { description?: string | null; name?: string | null }
    ) => {
      if (url && !seen.has(url)) {
        seen.add(url);
        images.push({ url, ...meta });
      }
    };

    if (update?.message) {
      extractMarkdownImageUrls(update.message).forEach(({ url }) => add(url));
    }

    update?.clarification?.files?.forEach((file: any) => {
      if (isFileImage(file))
        add(file.url, { description: file.description, name: file.name });
    });

    update?.question?.embedded_files?.forEach((file: any) => {
      if (isFileImage(file))
        add(file.url, { description: file.description, name: file.name });
    });

    update?.embedded_files?.forEach((file: any) => {
      if (isFileImage(file))
        add(file.url, { description: file.description, name: file.name });
    });

    return images;
  }, [
    update?.message,
    update?.clarification?.files,
    update?.question?.embedded_files,
    update?.embedded_files
  ]);

  // ClarificationContent renders its own non-image update.clarification.files;
  // the backend also syncs those onto update.embedded_files, so skip
  // duplicates here.
  const topLevelNonImageFiles = useMemo(() => {
    let files = (update?.embedded_files ?? []).filter((f) => !isFileImage(f));

    if (update.clarification) {
      const clarificationFileIds = new Set(
        (update.clarification.files ?? []).map((file: any) => file.id)
      );

      files = files.filter((f) => !clarificationFileIds.has(f.id));
    }

    return files;
  }, [update?.embedded_files, update.clarification]);

  const isUserQuestion = useMemo(() => {
    return update?.update_type === 'user-question';
  }, [update?.update_type]);

  const updateType = useMemo(() => {
    return getUpdateStatus(update);
  }, [update]);

  const questionTitle = useMemo(() => {
    return update?.title || 'Question';
  }, [update?.title]);

  return (
    <div className="flex flex-col relative flex-1" key={update?.id}>
      <div className="flex gap-4 h-full px-6">
        <div className="flex flex-col flex-1 h-full relative">
          <UserAvatar
            className="size-11 min-w-11 min-h-11"
            size="lg"
            name={getCreatorName()}
            image={getCreatorAvatar()}
            disableAnimation
            withTooltip
          />

          {withLine && <div className="w-[1px] h-full bg-gray-4 mx-auto" />}
        </div>
        <div className="flex flex-col w-full">
          <div className="flex flex-col w-full flex-1 gap-3">
            <div className="mt-1 flex gap-2 items-start justify-between">
              <Typography variant="h3" weight="bold">
                {isUserQuestion ? questionTitle : update?.title}
              </Typography>
              {updateType !== 'resolved' &&
                (isQuestion || update.update_type !== 'block') && (
                  <Tooltip
                    content="This question can be marked as resolved once it's answered."
                    isDisabled={!isQuestion || isAnswered}
                  >
                    <div className="inline-flex">
                      <ButtonV2
                        size="lg"
                        color="primary"
                        onPress={onResolveUpdate}
                        isDisabled={
                          updateMutation.isPending ||
                          (isQuestion && !isAnswered)
                        }
                        isLoading={updateMutation.isPending}
                        spinnerPlacement="end"
                        spinner={
                          <Spinner size="sm" color="white" variant="dots" />
                        }
                        className="min-w-36"
                      >
                        Mark as resolved
                      </ButtonV2>
                    </div>
                  </Tooltip>
                )}
            </div>

            <MarkdownContent markdownString={update?.message} hideImages />

            {allImages.length > 0 && <ImageGallery imageUrls={allImages} />}

            {topLevelNonImageFiles.length > 0 && (
              <div className="flex gap-2 items-center flex-wrap">
                {topLevelNonImageFiles.map((file) => (
                  <FileAttachment
                    key={file.id}
                    fileName={file.name}
                    fileUrl={file.url}
                    description={file.description}
                  />
                ))}
              </div>
            )}

            <div className="flex flex-col relative">
              <div className="w-[2.3px] rounded-full bottom-0 left-[-39px] right-0 absolute z-10 bg-white" />

              <div>
                {!isComplete &&
                  !update.question?.answer &&
                  update?.question?.question_type !== 'text' &&
                  renderQuestion()}
                {!isComplete && renderClarification()}
              </div>

              <div className="flex items-center gap-2">
                <Typography
                  variant="body-1"
                  className="text-secondary-text whitespace-nowrap"
                >
                  {format(update?.created_at, 'MMM dd, yyyy')}
                </Typography>

                {supplierCloaks && (
                  <div className="flex items-center gap-1">
                    <div className="min-h-[6px] min-w-[6px] bg-gray-4 rounded-full" />
                    <div className="flex items-center gap-[2.5px]">
                      <UserIcon className="size-4 text-secondary-text" />
                      <Typography
                        variant="body-1"
                        className="text-secondary-text whitespace-nowrap"
                      >
                        {supplierCloaks}
                      </Typography>
                    </div>
                  </div>
                )}

                {linkedBulkOrders.map((linkedBulkOrder) => (
                  <React.Fragment key={linkedBulkOrder.id}>
                    <div className="min-h-[6px] min-w-[6px] bg-gray-4 rounded-full" />
                    <Link
                      to={`${ROUTE_PATHS.PRODUCTS}/${linkedBulkOrder.product_id ?? routeProductId}?tab=orders&order=${linkedBulkOrder.id}`}
                      className="flex items-center gap-[2.5px] cursor-pointer text-secondary-text hover:text-primary"
                    >
                      <LinkIcon className="size-4 text-inherit" />
                      <Typography
                        variant="body-1"
                        className="text-inherit whitespace-nowrap"
                      >
                        {linkedBulkOrder.name}
                      </Typography>
                    </Link>
                  </React.Fragment>
                ))}

                {linkedSamples.map((linkedSample) => (
                  <React.Fragment key={linkedSample.id}>
                    <div className="min-h-[6px] min-w-[6px] bg-gray-4 rounded-full" />
                    <Link
                      to={`${ROUTE_PATHS.PRODUCTS}/${linkedSample.product_id ?? routeProductId}?tab=samples&sample=${linkedSample.id}`}
                      className="flex items-center gap-[2.5px] cursor-pointer text-secondary-text hover:text-primary"
                    >
                      <LinkIcon className="size-4 text-inherit" />
                      <Typography
                        variant="body-1"
                        className="text-inherit whitespace-nowrap"
                      >
                        {linkedSample.name}
                      </Typography>
                    </Link>
                  </React.Fragment>
                ))}
              </div>

              <UpdateThread update={update} />
            </div>
          </div>
        </div>
      </div>

      <div className="sticky bottom-0 left-0 right-0 z-30 bg-white p-6 pt-0 mt-6">
        <div className="relative">
          <div
            id="bottomOverlay"
            className="absolute top-[-40px] left-0 right-0 h-[40px] z-20 pointer-events-none"
            style={{
              background:
                'linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #FFFFFF 100%)'
            }}
          />
          {!update.question?.answer &&
          update?.question?.question_type === 'text' ? (
            renderQuestion()
          ) : (
            <ProductUpdatesInput
              hideCloseButton
              suppliers={[]}
              productId={routeProductId as string}
              clarificationId={update?.clarification?.id}
              isReplying
              updateId={update?.id}
              onScrollToBottom={onScrollToBottom}
            />
          )}
        </div>
      </div>
      {/* )} */}
    </div>
  );
};

export default ProductUpdateModalContent;
